import React from 'react';
import { Controller } from 'react-hook-form';
import * as yup from 'yup';

import Button from '@/core/components/Button';
import HintMessage from '@/core/components/HintMessage';
import CheckboxInput from '@/core/components/inputs/CheckboxInput';
import CityInput from '@/core/components/inputs/CityInput';
import TextInput from '@/core/components/inputs/TextInput';
import Label from '@/core/components/Label';
import useForm from '@/core/hooks/useForm';
import { useAuthContext } from '@/core/lib/auth/auth.context';
import { useEnvContext } from '@/core/lib/env/env.context';
import { ApiMethods } from '@/core/lib/fetch';
import { ApiVersions } from '@/core/lib/fetch/fetch';
import Query from '@/core/lib/new-architecture/query';
import { NOT_URL_REGEX, USERNAME_AT_LEAST_2_REGEX, USERNAME_REGEX } from '@/core/lib/regex';
import { useRouterContext } from '@/core/lib/router/router.context';
import Routes from '@/core/lib/router/routes';
import { useTranslationContext } from '@/core/lib/translation/translation.context';
import type { UsersHasControlResult } from '@/core/types/control';

const schema = yup.object({
  username: yup
    .string()
    .matches(USERNAME_AT_LEAST_2_REGEX, 'control.rs.username.required')
    .matches(USERNAME_REGEX, 'control.rs.username.required')
    .matches(NOT_URL_REGEX, 'errors.inputs.has_not_url')
    .required('control.rs.username.required'),
  loc: yup
    .object({
      loc: yup.string().required('control.rs.loc.required'),
    })
    .required('control.rs.loc.required'),
  cgu: yup.boolean().required('control.cgu.required').isTrue('control.cgu.required'),
  newsletter: yup.boolean().required(),
});

type Schema = yup.InferType<typeof schema>;

interface Data {
  username: string;
  loc: string;
}

const RsControl: React.FC = () => {
  const { logout } = useAuthContext();
  const { t } = useTranslationContext(['common']);
  const { DONNONS_API_URL } = useEnvContext();
  const { invalidate } = Query.user.useInvalidate();
  const { Link } = useRouterContext();

  const { control, onSubmit, errors } = useForm<UsersHasControlResult, Data, Schema>({
    schema,
    apiDefinition: { method: ApiMethods.PUT, url: `${DONNONS_API_URL}${ApiVersions.V1}/user/control/rs` },
    transformers: {
      formToApi: data => ({ ...data, loc: data.loc.loc, cgu: !!data.cgu }),
    },
    onSuccess: async () => {
      await invalidate();
    },
  });

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex w-full grow flex-col gap-6">
        <div className="mx-auto flex size-30 items-center justify-center rounded-full">
          <img loading="lazy" decoding="async" src="/assets/control/rs.svg" alt={t('control.rs.title', { ns: 'common' })} className="object-contain" />
        </div>
        <p className="text-body-primary text-center font-semibold text-content-secondary">{t('control.rs.title', { ns: 'common' })}</p>
        <p className="text-body-secondary font-normal text-content-primary">{t('control.rs.subtitle', { ns: 'common' })}</p>
        <form className="flex flex-col gap-4">
          <Controller
            control={control}
            render={({ field: { ref, value, onChange, onBlur } }) => {
              const { isError, message, isSuccess } = errors.username;
              return (
                <Label htmlFor="username" content={t('control.rs.username.label', { ns: 'common' })}>
                  <TextInput
                    id="username"
                    value={value ?? ''}
                    onChange={onChange}
                    onBlur={onBlur}
                    validation={errors.username}
                    ref={ref}
                    enterKeyHint="next"
                    placeholder={t('control.rs.username.placeholder', { ns: 'common' })}
                  />
                  {!isError && <HintMessage message={t('control.rs.username.hint', { ns: 'common' })} />}
                  {isError && <HintMessage isError={isError} isSuccess={isSuccess} message={message} />}
                </Label>
              );
            }}
            name="username"
            defaultValue=""
          />

          <Controller
            control={control}
            render={({ field: { ref, value, onChange, onBlur } }) => {
              const { isError, message, isSuccess } = errors.loc;
              return (
                <Label htmlFor="loc" content={t('control.rs.loc.label', { ns: 'common' })}>
                  <CityInput
                    id="loc"
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    validation={errors.loc}
                    ref={ref}
                    placeholder={t('control.rs.loc.placeholder', { ns: 'common' })}
                    cityInputType="limited"
                  />
                  <HintMessage isError={isError} isSuccess={isSuccess} message={message} />
                </Label>
              );
            }}
            name="loc"
          />

          <Controller
            control={control}
            render={({ field: { onChange, onBlur, value } }) => {
              const { isError, message, isSuccess } = errors.cgu;
              return (
                <>
                  <div className="flex items-center">
                    <CheckboxInput onChange={onChange} onBlur={onBlur} id="cgu" value={value} validation={{ isError, isSuccess }}>
                      <p className="text-caption-primary">{t('control.rs.cgu.label', { ns: 'common' })}</p>
                    </CheckboxInput>
                    <Link href={new Routes.CguRoute()} isInAppBrowser key="cgu" target="_blank" className="text-content-secondary">
                      {t('control.rs.cgu.link', { ns: 'common' })}
                    </Link>
                  </div>
                  {isError && <HintMessage isError={isError} isSuccess={isSuccess} message={message} />}
                </>
              );
            }}
            name="cgu"
          />

          <Controller
            control={control}
            defaultValue={false}
            render={({ field: { onChange, onBlur, value } }) => {
              const { isError, isSuccess } = errors.newsletter;
              return (
                <>
                  <CheckboxInput onChange={onChange} onBlur={onBlur} id="newsletter" value={value} validation={{ isError, isSuccess }}>
                    <div className="text-caption-primary">{t('control.rs.newsletter', { ns: 'common' })}</div>
                  </CheckboxInput>
                </>
              );
            }}
            name="newsletter"
          />
        </form>
      </div>
      <div className="flex gap-4">
        <Button variant="secondary" onClick={logout}>
          {t('control.rs.cancel', { ns: 'common' })}
        </Button>
        <Button variant="primary" onClick={onSubmit}>
          {t('control.rs.submit', { ns: 'common' })}
        </Button>
      </div>
    </div>
  );
};

export default RsControl;
